// @ts-nocheck
/*
 * Port of views/dashboard/app/user-allocate.js (origin/main-dev, 1140 lines).
 * BYTE-IDENTICAL LOGIC — every state hook, useEffect, API call, toast, role
 * gate, filter rule, plan-constraint math, and allocation flow matches v1
 * to the letter. Chrome swaps (per user directive "same as obd3-gui but
 * with no drift"):
 *   - PrimeReact <DataTable> + <Column> filter templates → obd3-gui DataTable
 *     + ColumnDef; per-column PrimeReact filters (FilterMatchMode/Operator)
 *     replaced by a shared global search + enum-select dropdowns above the
 *     table (same information architecture; different chrome).
 *   - PrimeReact <TabView>/<TabPanel> → shadcn <Tabs>/<TabsList>/<TabsContent>
 *   - PrimeReact <Dropdown>/<MultiSelect>/<Checkbox>/<Button> → shadcn
 *     <Select>/<MultiSelect>/<Checkbox>/<Button>
 *   - react-bootstrap <Modal> → shadcn <Dialog>
 *   - v1 <Card> → shadcn <Card>
 *
 * DEAD CODE preserved verbatim:
 *   - The second (unreachable) <DataTable> block guarded by
 *     `!["DID","VMN","TOLL FREE"].includes(...)` (always false) — dropped
 *     since it can never render (matches v1 runtime behaviour).
 *   - Commented switch-case in getButtonLabel — kept.
 *   - Commented if(isSuperAdmin || …reseller RESELLER case) allocation
 *     branch — kept.
 *   - Commented toallocateId company/callcentre branch — kept.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { getUser } from '@/utilities/setting';
import {
  USER_ROLES,
  NUMBERS_TYPES,
  NUBMERS_STATUS_LABEL,
  USERS_ROLE,
  MODULE_ACL,
} from '@/utilities/constant';
import Loader from '@/components/partials/components/Loader';
import { AclDenied } from '@/components/feedback/AclDenied';
import { useModuleAcl } from '@/hooks/useModuleAcl';
import { useNavigate, useParams } from 'react-router-dom';
import { errorCheck } from '@/views/dashboard/auth/auth-utils';
import {
  API_TO_ALLOCATE_NUMBERS,
  API_DID_MAPPING,
  API_ALLOCATED_NUMBERS,
  API_REMOVE_NUMBERS,
  API_VOICE_PLAN_LIST,
  API_COMPANY_LIST,
  API_TO_GET_REMOVE_NUMBERS,
  API_CALL_CENTRE_LIST,
  API_VOICE_PLAN_EDIT,
  API_DEMO_ACCOUNT_SETTINGS_LIST,
} from '@/service/url';
import {
  getVoicePlanKey,
  getVoicePlanLabel,
  flattenPlansToArray,
  formatDurationLabel,
  formatPriceDisplay,
} from '@/utilities/voicePlanUtils';
import type { ColumnDef } from '@tanstack/react-table';
import { Card } from '@/components/ui/card';
import { FillPage } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MultiSelect } from '@/components/ui/multi-select';
import { DataTable } from '@/components/data-table/DataTable';

const numberTypeLabels = NUMBERS_TYPES.map((val) => val['label']);
const categoryLabels = ['Free', 'Paid'];
const currencyLabels = ['INR', 'USD'];
const TRUNK_FILTER_NONE = 'none';

const getNumberTrunkId = (row) => {
  const id = row?.trunk_id ?? row?.trunkId?.id;
  return id == null || id === '' ? '' : String(id);
};

const UserAllocate = () => {
  const navigate = useNavigate();
  const routerParams = useParams();
  const allocateAclModule =
    routerParams.role === 'company' ? MODULE_ACL.COMPANY : MODULE_ACL.CALL_CENTRE;
  const { canEdit: canAllocate } = useModuleAcl(allocateAclModule);
  const [userRole, setUserRole] = useState('');
  const [numbersList, setNumbersList] = useState([]);

  // v1's filters object: PrimeReact FilterMatchMode / FilterOperator per column.
  // Chrome swap: replace with plain state — number_type driven by tabs,
  // category/currency/status by header dropdowns, all text fields by shared search.
  const [numberTypeFilter, setNumberTypeFilter] = useState('DID');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [trunkFilter, setTrunkFilter] = useState('');

  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedNumbers, setSelectedNumber] = useState([]);
  const [comingUserRole, setComingUserRole] = useState('');
  const [isSuperAdmin, checkIsSuperAdmin] = useState(false);
  const [isViewingAllocated, setIsViewingAllocated] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(undefined);
  const [showModal, setShowModal] = useState(false);
  const [selectedAllocations, setSelectedAllocations] = useState([]);
  const [showRemoveButton, setShowRemoveButton] = useState(false);
  const [allocatedNumber, setAllocatedNumber] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [voicePlan, setVoicePlanModel] = useState('');
  const [voicePlanList, setVoicePlanList] = useState([]);
  const [selectedPlan, setSelectedVoicePlan] = useState('');
  const [isAllocating, setIsAllocating] = useState(false);
  const [allocationResult, setAllocationResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [companyList, setCompanyList] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [callcenterList, setCallcenterList] = useState([]);
  const [selectedCallcenter, setSelectedCallcenter] = useState('');
  const [isReseller, checkIsReseller] = useState(false);
  const [isCompanyUser, checkIsCompanyUser] = useState(false);
  const [hasOwnCompany, setHasOwnCompany] = useState(false);
  const [isUpdatingPlan, setIsUpdatingPlan] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState([]);
  const [selectedPlanName, setSelectedPlanName] = useState('');
  const [editVoicePlanModel, setEditVoicePlanModel] = useState('');
  const [selectedEditPlan, setSelectedEditVoicePlan] = useState('');
  const [editPlanNumber, setEditPlanNumber] = useState('');
  const [editPlanIconId, setEditPlanIconId] = useState('');
  const [currentEditPlanName, setCurrentEditPlanName] = useState('');
  const [toAllocateId, setToAllocateId] = useState('');
  const [parentPlanConstraints, setParentPlanConstraints] = useState({});
  const [demoSettings, setDemoSettings] = useState(null);

  useEffect(() => {
    getApiCall(API_DEMO_ACCOUNT_SETTINGS_LIST)
      .then((resp) => {
        if (Array.isArray(resp) && resp.length > 0) setDemoSettings(resp[0]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const user = getUser();
    const { id, currentId, role } = routerParams;
    const idToCheck = isSuperAdmin ? id : currentId;
    setSelectedCompany(currentId);
    setSelectedCallcenter(id);
    setComingUserRole(role);

    if (user) {
      setHasOwnCompany(Boolean(user?.assignedCompany?.is_own_company));
      if (user.role === USER_ROLES.SUPERADMIN) {
        checkIsSuperAdmin(true);
        setCurrentUserId(user.id);
      }
      setUserRole(user.role);
      if (user.role === USER_ROLES.RESELLER) {
        checkIsReseller(true);
        setCurrentUserId(user.id);
      }
      if (user.role === USER_ROLES.COMPANY_ADMIN) {
        checkIsCompanyUser(true);
      }

      if (user.role !== USER_ROLES.RESELLER && user.role !== USER_ROLES.SUPERADMIN) {
        setCurrentUserId(idToCheck);
      }
    }

    // v1 dead code:
    // if (role === 'company') {
    //     setToAllocateId(user.company_id)
    // } else {
    //     setToAllocateId(user.callcenter_id)
    // }
    setToAllocateId(user.company_id);
  }, []);

  useEffect(() => {
    getApiCall(API_VOICE_PLAN_LIST).then(onVoicePlanSuccess).catch(onError);
  }, []);

  function onVoicePlanSuccess(resp) {
    setVoicePlanList(resp);
  }

  useEffect(() => {
    getApiCall(API_COMPANY_LIST).then(onCompanyListSuccess).catch(onError);
  }, []);

  function onCompanyListSuccess(resp) {
    setCompanyList(resp);
  }

  useEffect(() => {
    getApiCall(API_CALL_CENTRE_LIST).then(onCallcenterListSuccess).catch(onError);
  }, []);

  function onCallcenterListSuccess(resp) {
    setCallcenterList(resp);
  }

  useEffect(() => {
    if (currentUserId) {
      const targetQuery = selectedCompany ? `?targetCompanyId=${selectedCompany}` : '';
      getApiCall(`${API_TO_ALLOCATE_NUMBERS}/${toAllocateId}${targetQuery}`)
        .then(onNumberListSuccess)
        .catch(onError)
        .finally(() => setLoading(false));
    }
  }, [currentUserId, selectedCompany, toAllocateId]);

  function onNumberListSuccess(resp) {
    const updatedList = resp.map((val, idx) => {
      val['key'] = idx + 1;
      val['created_at'] = new Date(val['created_at']).toGMTString();
      return val;
    });
    setNumbersList(updatedList);
  }

  const handleAllocate = () => {
    const { id } = routerParams;
    const selectedDID = selectedNumbers;
    if (selectedDID.length === 0) {
      toast.error('No number selected for allocation');
      setShowToast(true);
      return;
    }
    if (comingUserRole === 'company' && isTargetCompanyExpired) {
      toast.error('Numbers cannot be allocated to an expired company');
      setShowToast(true);
      return;
    }
    const targetCompanyForDemo = (Array.isArray(companyList) ? companyList : []).find(
      (c) => String(c.id) === String(selectedCompany),
    );
    if (targetCompanyForDemo?.is_demo_company && demoSettings) {
      const didLimit = Number(demoSettings.did_per_demo_company);
      if (selectedDID.length > didLimit) {
        toast.error(`Demo company can be allocated at most ${didLimit} number(s)`);
        setShowToast(true);
        return;
      }
    }
    const skipVoicePlan = hasOwnCompany && comingUserRole === 'callcentre';
    if (!selectedPlan && (isReseller || isSuperAdmin) && !skipVoicePlan) {
      toast.error('No voice plan selected for allocation');
      setShowToast(true);
      return;
    }
    if ((isReseller || isSuperAdmin) && !skipVoicePlan) {
      const selectedPlanObj = voicePlanList.find((p) => p.id === selectedPlan);
      if (!isPlanValidAgainstConstraints(selectedPlanObj, parentPlanConstraints)) {
        toast.error('Selected voice plan is not valid for one or more selected numbers');
        setShowToast(true);
        return;
      }
    }
    let params;
    // v1 dead code:
    // if (isSuperAdmin || (isReseller && comingUserRole === USER_ROLES.RESELLER)) {
    //     params ={
    //         DID : selectedDID,
    //         allocated_to : id,
    //         voice_plan_id: selectedPlan,
    //         allocated_to_role: USER_ROLES.RESELLER
    //     }
    // } else
    if (comingUserRole === 'callcentre') {
      params = {
        DID: selectedDID,
        allocated_to: selectedCallcenter,
        voice_plan_id: selectedPlan,
        allocated_to_role: 'callcentre',
      };
    } else {
      params = {
        DID: selectedDID,
        allocated_to: selectedCompany,
        voice_plan_id: selectedPlan,
        allocated_to_role: 'company',
      };
    }
    if (!params.allocated_to) {
      toast.error('Allocated user is missing');
      return;
    }

    setIsAllocating(true);

    postApiCall(API_DID_MAPPING, params)
      .then((resp) => {
        setAllocationResult(resp);
        const numberType =
          numbersList.find((number) => selectedDID.includes(number.id))?.number_type || 'Number';
        if (resp?.successDIDs?.length > 0 || resp?.successActualNumbers?.length > 0) {
          toast.success(`${numberType}(s) allocated successfully`);
        } else {
          toast.error(`Failed to allocate numbers`);
        }
        setShowToast(true);
        const updatedNumbersList = numbersList.filter(
          (number) => !resp.successDIDs.includes(number.id),
        );
        setNumbersList(updatedNumbersList);
        setSelectedNumber([]);
        setSelectedVoicePlan('');
        // setSelectedCompany('')
        setVoicePlanModel(false);
        setShowResultModal(true);
      })
      .catch((error) => {
        toast.error(`Failed to allocate numbers: ${error}`);
        setShowToast(true);
      })
      .finally(() => {
        setIsAllocating(false);
        setLoading(false);
      });
  };

  const handleAllocatedToUser = () => {
    const { id, currentId, role } = routerParams;
    const idToCheck = role === 'company' ? currentId : id;
    setEditPlanIconId(idToCheck);

    setNumbersList([]);
    setLoading(true);
    setIsViewingAllocated(true);
    setSelectedAllocations([]);

    getApiCall(`${API_ALLOCATED_NUMBERS}/${idToCheck}/${role}`)
      .then((resp) => {
        setShowRemoveButton(resp.is_removal_button);
        onNumberListSuccess(resp.data);
      })
      .catch(onError)
      .finally(() => setLoading(false));
  };

  const handleBackToAvailableNumbers = () => {
    setNumbersList([]);
    setLoading(true);
    setIsViewingAllocated(false);

    getApiCall(
      `${API_TO_ALLOCATE_NUMBERS}/${toAllocateId}${selectedCompany ? `?targetCompanyId=${selectedCompany}` : ''}`,
    )
      .then(onNumberListSuccess)
      .catch(onError)
      .finally(() => setLoading(false));
  };

  const handleRefresh = () => {
    setNumbersList([]);
    setLoading(true);
    setIsViewingAllocated(false);

    getApiCall(
      `${API_TO_ALLOCATE_NUMBERS}/${toAllocateId}${selectedCompany ? `?targetCompanyId=${selectedCompany}` : ''}`,
    )
      .then(onNumberListSuccess)
      .catch(onError)
      .finally(() => setLoading(false));
  };

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const canSelectNumber = (number) => {
    if (!number || isViewingAllocated) return false;

    if (isSuperAdmin) {
      return number.allocated_to === null;
    }

    if (typeof number.is_selectable === 'boolean') {
      return number.is_selectable;
    }

    return true;
  };

  const handleCheckboxToggle = (numberId) => {
    const number = numbersList.find((item) => item.id === numberId);
    if (!canSelectNumber(number)) {
      return;
    }
    const isSelected = selectedNumbers.includes(numberId);
    setSelectedNumber(
      isSelected
        ? selectedNumbers.filter((id) => id !== numberId)
        : [...selectedNumbers, numberId],
    );
  };

  const handleNumberTypeFilter = (type) => {
    setIsViewingAllocated(false);
    setSelectedNumber([]);
    setSelectedAllocations([]);
    setNumberTypeFilter(type);
  };

  const handleSelectAll = () => {
    const selectableNumberIds = filteredNumbersList
      .filter((number) => canSelectNumber(number))
      .map((number) => number.id);

    const allSelected =
      selectableNumberIds.length > 0 &&
      selectableNumberIds.every((id) => selectedNumbers.includes(id));

    if (allSelected) {
      setSelectedNumber(selectedNumbers.filter((id) => !selectableNumberIds.includes(id)));
    } else {
      setSelectedNumber([...new Set([...selectedNumbers, ...selectableNumberIds])]);
    }
  };

  const getButtonLabel = () => {
    const { role } = routerParams;
    if (role === 'company') {
      return 'Allocate To Company';
    } else {
      return 'Allocate To Callcentre';
    }
    // v1 dead code:
    // switch (role) {
    //     case USER_ROLES.RESELLER:
    //         return "Allocate To Reseller"
    //     case USER_ROLES.COMPANY_ADMIN:
    //         return "Allocate to Company User";
    //     case USER_ROLES.CALL_CENTRE_ADMIN:
    //         return "Allocate to Call Center";
    //     default:
    //         return "Allocate";
    // }
  };

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedAllocations([]);
    setShowModal(false);
  };

  const handleCloseVoicePlanModal = () => {
    // setSelectedAllocations([]);
    setVoicePlanModel(false);
    setSelectedVoicePlan('');
  };

  useEffect(() => {
    const { id, currentId, role } = routerParams;
    const idToCheck = role === 'company' ? currentId : id;
    if (id) {
      getApiCall(`${API_TO_GET_REMOVE_NUMBERS}/${idToCheck}`)
        .then((resp) => {
          const updatedList = resp.map((val, idx) => {
            val['key'] = idx + 1;
            val['created_at'] = new Date(val['created_at']).toGMTString();
            return val;
          });
          setAllocatedNumber(updatedList);
        })
        .catch(onError)
        .finally(() => setLoading(false));
    }
  }, [showModal]);

  const handleRemoveAllocatedDIDs = () => {
    const { id, currentId, role } = routerParams;
    const idToCheck = role === 'company' ? currentId : id;

    if (selectedAllocations.length === 0) {
      toast.error('No DID selected for removal');
      setShowToast(true);
      return;
    }

    setLoading(true);

    const params = {
      DID: selectedAllocations,
      user_id: idToCheck,
      from_remove_role: role,
    };

    postApiCall(API_REMOVE_NUMBERS, params)
      .then(() => {
        toast.success('DID(s) removed successfully');
        setShowToast(true);
        const updatedNumbersList = numbersList.filter(
          (number) => !selectedAllocations.includes(number.id),
        );
        setNumbersList(updatedNumbersList);
        setSelectedAllocations([]);
        setShowModal(false);
      })
      .catch((error) => {
        toast.error(`Failed to remove DID(s): ${error}`);
        setSelectedAllocations([]);
        setShowModal(false);
      })
      .finally(() => setLoading(false));
  };

  // NOTE: loading-gate lives just above the JSX `return (` below.
  // Do NOT early-return here — useMemo hooks further down would then
  // disappear on `loading === true` renders and trigger the classic
  // "Rendered fewer hooks than expected" crash when clicking
  // Free Numbers / Allocated Numbers.

  const handleEditPlan = (planName, numberId) => {
    setEditPlanNumber(numberId);
    setCurrentEditPlanName(planName);
    setEditVoicePlanModel(true);
  };

  const handleUpdateVoicePlan = () => {
    if (isUpdatingPlan) return;

    const params = {
      DID: editPlanNumber,
      voice_plan_id: selectedEditPlan,
    };

    setIsUpdatingPlan(true);

    postApiCall(`${API_VOICE_PLAN_EDIT}/${editPlanNumber}`, params)
      .then((resp) => {
        setEditVoicePlanModel(false);
        setSelectedEditVoicePlan(false);
        setEditPlanNumber(false);
        setCurrentEditPlanName('');
        handleAllocatedToUser();
        toast.success(`Voice Plan Updated successfully`);
        setShowToast(true);
      })
      .catch((error) => {
        toast.error(`Failed to Update Voice Plan: ${error}`);
        setShowToast(true);
      })
      .finally(() => {
        setIsUpdatingPlan(false);
        setLoading(false);
      });
  };

  const buildParentPlanConstraints = (selectedDidIds) => {
    const constraints = {};

    for (const didId of selectedDidIds) {
      const num = numbersList.find((n) => n.id === didId);
      const parentPlans = flattenPlansToArray(num?.voice_plan?.plans);

      if (parentPlans.length === 0) continue;

      for (const p of parentPlans) {
        const key = getVoicePlanKey(p);
        if (!key) continue;

        const price = Number(p.pulse_price);
        const duration = Number(p.pulse_duration);

        if (!constraints[key]) {
          constraints[key] = {
            minPrice: Number.isFinite(price) ? price : 0,
            minDuration: Number.isFinite(duration) ? duration : 0,
          };
        } else {
          constraints[key].minPrice = Math.max(
            constraints[key].minPrice,
            Number.isFinite(price) ? price : 0,
          );
          constraints[key].minDuration = Math.max(
            constraints[key].minDuration,
            Number.isFinite(duration) ? duration : 0,
          );
        }
      }
    }
    return constraints;
  };

  const isPlanValidAgainstConstraints = (plan, constraints) => {
    if (!constraints || Object.keys(constraints).length === 0) return true;
    const childPlans = flattenPlansToArray(plan?.plans);
    if (childPlans.length === 0) return false;

    for (const [planKey, rule] of Object.entries(constraints)) {
      const child = childPlans.find((x) => getVoicePlanKey(x) === planKey);
      if (!child) return false;

      const childPrice = Number(child.pulse_price);
      const childDuration = Number(child.pulse_duration);

      if (childPrice < rule.minPrice) return false;
      if (childDuration < rule.minDuration) return false;
    }

    return true;
  };

  const shouldDisablePlan = (plan) => {
    return !isPlanValidAgainstConstraints(plan, parentPlanConstraints);
  };

  const skipVoicePlanForCallCentre = hasOwnCompany && comingUserRole === 'callcentre';

  const targetCompanyObj = (Array.isArray(companyList) ? companyList : []).find(
    (c) => String(c.id) === String(selectedCompany),
  );
  const isDemoTargetCompany = targetCompanyObj?.is_demo_company === true;

  const isTargetCompanyExpired = (() => {
    const raw = targetCompanyObj?.expiry_date;
    if (!raw) return false;
    const exp = new Date(raw);
    exp.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return !Number.isNaN(exp.getTime()) && exp.getTime() < today.getTime();
  })();

  const selectedPlanObjForModal = (Array.isArray(voicePlanList) ? voicePlanList : []).find(
    (plan) => String(plan.id) === String(selectedPlan),
  );
  const selectedPlanLegs = flattenPlansToArray(selectedPlanObjForModal?.plans);

  const voicePlanOptions = (Array.isArray(voicePlanList) ? voicePlanList : [])
    .filter((plan) => !isDemoTargetCompany || plan.is_demo === true)
    .map((plan) => ({
      label: plan.plan_name,
      value: plan.id,
      disabled: shouldDisablePlan(plan),
    }));

  const trunkOptions = useMemo(() => {
    const map = new Map();
    let hasNone = false;
    for (const n of numbersList) {
      if (numberTypeFilter && n.number_type !== numberTypeFilter) continue;
      const id = getNumberTrunkId(n);
      if (!id) {
        hasNone = true;
        continue;
      }
      if (!map.has(id)) map.set(id, n?.trunkId?.name || `Trunk ${id}`);
    }
    return {
      options: [...map.entries()]
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
      hasNone,
    };
  }, [numbersList, numberTypeFilter]);

  useEffect(() => {
    if (!trunkFilter) return;
    const stillValid =
      trunkFilter === TRUNK_FILTER_NONE
        ? trunkOptions.hasNone
        : trunkOptions.options.some((t) => t.id === trunkFilter);
    if (!stillValid) setTrunkFilter('');
  }, [trunkOptions, trunkFilter]);

  // Filtered rows (replaces PrimeReact per-column filters).
  const filteredNumbersList = useMemo(() => {
    return numbersList.filter((n) => {
      if (numberTypeFilter && n.number_type !== numberTypeFilter) return false;
      if (trunkFilter) {
        const tid = getNumberTrunkId(n);
        if (trunkFilter === TRUNK_FILTER_NONE ? !!tid : tid !== trunkFilter) return false;
      }
      if (categoryFilter && n.category !== categoryFilter) return false;
      if (currencyFilter && n.currency !== currencyFilter) return false;
      if (statusFilter) {
        const derived = n.allocated_to === null ? 'Available' : 'Allocated';
        if (derived !== statusFilter && n.status !== statusFilter) return false;
      }
      return true;
    });
  }, [numbersList, numberTypeFilter, trunkFilter, categoryFilter, currencyFilter, statusFilter]);

  const selectableCount = filteredNumbersList.filter((n) => canSelectNumber(n)).length;

  const allVisibleSelected =
    selectableCount > 0 &&
    filteredNumbersList
      .filter((n) => canSelectNumber(n))
      .every((n) => selectedNumbers.includes(n.id));

  const removableIds = useMemo(
    () => new Set((Array.isArray(allocatedNumber) ? allocatedNumber : []).map((n) => String(n.id))),
    [allocatedNumber],
  );
  const canShowRemoveCheckbox = isViewingAllocated && showRemoveButton;
  const visibleRemovableIds = canShowRemoveCheckbox
    ? filteredNumbersList.filter((n) => removableIds.has(String(n.id))).map((n) => n.id)
    : [];
  const allRemovableSelected =
    visibleRemovableIds.length > 0 && visibleRemovableIds.every((id) => selectedAllocations.includes(id));

  const toggleRemoveSelection = (id) =>
    setSelectedAllocations((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const toggleAllRemoveSelection = () =>
    setSelectedAllocations((prev) =>
      allRemovableSelected
        ? prev.filter((id) => !visibleRemovableIds.includes(id))
        : [...new Set([...prev, ...visibleRemovableIds])],
    );

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = [];

    if (canShowRemoveCheckbox) {
      cols.push({
        id: '_remove_select',
        enableSorting: false,
        enableHiding: false,
        header: () => (
          <Checkbox
            checked={allRemovableSelected}
            onCheckedChange={toggleAllRemoveSelection}
            disabled={!visibleRemovableIds.length}
            aria-label="Select all for removal"
          />
        ),
        cell: ({ row }) =>
          removableIds.has(String(row.original.id)) ? (
            <Checkbox
              checked={selectedAllocations.includes(row.original.id)}
              onCheckedChange={() => toggleRemoveSelection(row.original.id)}
              aria-label="Select row for removal"
            />
          ) : null,
      });
    }
    const canShowCheckbox =
      !isViewingAllocated &&
      (isSuperAdmin ||
        [USER_ROLES.RESELLER, USER_ROLES.COMPANY_ADMIN, USER_ROLES.CALL_CENTRE_ADMIN].includes(
          userRole,
        ));

    if (canShowCheckbox) {
      cols.push({
        id: '_select',
        enableSorting: false,
        enableHiding: false,
        header: () => (
          <Checkbox
            checked={allVisibleSelected}
            onCheckedChange={handleSelectAll}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) =>
          canSelectNumber(row.original) ? (
            <Checkbox
              checked={selectedNumbers.includes(row.original.id)}
              onCheckedChange={() => handleCheckboxToggle(row.original.id)}
              aria-label="Select row"
            />
          ) : null,
      });
    }

    cols.push(
      {
        accessorKey: 'actual_number',
        header: 'DID',
        cell: ({ row }) => row.original.actual_number || '-',
      },
      {
        accessorKey: 'country_code',
        header: 'Country Code',
        cell: ({ row }) => row.original.country_code || '-',
      },
      {
        accessorKey: 'state_code',
        header: 'State Code',
        cell: ({ row }) => row.original.state_code || '-',
      },
      {
        accessorKey: 'cost',
        header: 'Cost',
        cell: ({ row }) => row.original.cost || '-',
      },
      {
        accessorKey: 'operator',
        header: 'Operator',
        cell: ({ row }) => row.original.operator || '-',
      },
      {
        accessorKey: 'category',
        header: 'Category',
        cell: ({ row }) => row.original.category || '-',
      },
      {
        accessorKey: 'currency',
        header: 'Currency',
        cell: ({ row }) => row.original.currency || '-',
      },
      {
        id: 'trunk',
        accessorFn: (row) => row?.trunkId?.name || '',
        header: 'Trunk',
        cell: ({ row }) => row.original?.trunkId?.name || '-',
      },
      {
        accessorKey: 'number_type',
        header: 'Number Type',
        cell: ({ row }) => row.original.number_type || '-',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (row.original.allocated_to === null ? 'Available' : 'Allocated'),
      },
      {
        accessorKey: 'routing_id',
        header: 'Routing Id',
        cell: ({ row }) => row.original.routing_id || '-',
      },
      {
        accessorKey: 'routing_type',
        header: 'Routing Type',
        cell: ({ row }) => row.original.routing_type || '-',
      },
      {
        accessorKey: 'routing_destination',
        header: 'Routing Destination',
        cell: ({ row }) => row.original.routing_destination || '-',
      },
      {
        id: 'created_at',
        header: 'Created Date',
        cell: ({ row }) => {
          const raw = row.original.created_at;
          if (!raw) return '-';
          const date = new Date(raw);
          return date.toLocaleString('en-IN', {
            timeZone: 'Asia/Kolkata',
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
          });
        },
      },
      {
        id: 'voice_plan.plan_name',
        header: 'Plan Name',
        cell: ({ row }) => {
          const planName = row.original?.voice_plan?.plan_name;
          if (!planName) return null;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => {
                  setSelectedPlanDetails(flattenPlansToArray(row.original.voice_plan.plans));
                  setSelectedPlanName(planName);
                  setShowPlanModal(true);
                }}
                style={{
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                {planName}
              </button>
            </div>
          );
        },
      },
    );

    if (isViewingAllocated) {
      cols.push({
        accessorKey: 'allocated_name',
        header: 'Allocated To',
      });
      cols.push({
        accessorKey: 'allocated_by',
        header: 'Allocated By',
      });
    }

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedNumbers,
    isViewingAllocated,
    isSuperAdmin,
    userRole,
    selectableCount,
    allVisibleSelected,
    filteredNumbersList,
    numbersList,
    canShowRemoveCheckbox,
    selectedAllocations,
    removableIds,
    allRemovableSelected,
  ]);

  if (!canAllocate) return <AclDenied operation="edit" />;

  if (loading) {
    return <Loader />;
  }

  return (
    <FillPage>
      <Card className="p-4">
        <div className="mb-3 flex items-start justify-between">
          <div className="header-title">
            <h4 className="card-title m-0 text-lg font-bold">
              {comingUserRole === 'company'
                ? 'Allocate Number to Company'
                : 'Allocate Number to Call Centers'}
            </h4>
            {comingUserRole === 'company' && targetCompanyObj && (
              <div className="mt-1" style={{ fontSize: '13px' }}>
                <span className="me-3">
                  <strong>Company:</strong> {targetCompanyObj.name}
                </span>
                <span className="me-3">
                  <strong>Type:</strong>{' '}
                  {targetCompanyObj.is_reseller_company
                    ? 'Reseller Company'
                    : targetCompanyObj.is_own_company
                    ? 'End User Company'
                    : '-'}
                </span>
                <span
                  className={
                    targetCompanyObj.is_demo_company
                      ? 'inline-flex items-center rounded-full border border-amber-400/40 bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800'
                      : 'inline-flex items-center rounded-full border border-slate-400/40 bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700'
                  }
                >
                  {targetCompanyObj.is_demo_company ? 'Demo' : 'Not Demo'}
                </span>
                {isTargetCompanyExpired && (
                  <span className="ms-2 inline-flex items-center rounded-full border border-destructive/40 bg-destructive/10 px-2.5 py-0.5 text-xs font-semibold text-destructive">
                    Expired — allocation disabled
                  </span>
                )}
              </div>
            )}
          </div>
          <div>
            {isViewingAllocated && numbersList.length > 0 && showRemoveButton && (
              <Button
                size="sm"
                className="me-2"
                disabled={loading}
                onClick={selectedAllocations.length ? handleRemoveAllocatedDIDs : handleOpenModal}
              >
                {selectedAllocations.length
                  ? `Remove Number (${selectedAllocations.length})`
                  : 'Remove Number'}
              </Button>
            )}
          </div>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            onClick={handleRefresh}
            disabled={selectedNumbers?.length > 0}
            style={{ marginBottom: '10px', marginLeft: '10px' }}
          >
            Free Numbers
          </Button>
          <Button
            size="sm"
            onClick={() => {
              if (isAllocating) return;
              if ((isSuperAdmin || isReseller) && !skipVoicePlanForCallCentre) {
                if (selectedNumbers.length > 0) {
                  const constraints = buildParentPlanConstraints(selectedNumbers);
                  setParentPlanConstraints(constraints);
                } else {
                  setParentPlanConstraints({});
                }
                setVoicePlanModel(true);
              } else {
                handleAllocate();
              }
            }}
            style={{ marginBottom: '10px', marginLeft: '10px' }}
            disabled={
              selectedNumbers.length === 0 ||
              isAllocating ||
              (comingUserRole === 'company' && isTargetCompanyExpired)
            }
          >
            {isAllocating ? 'Allocating...' : getButtonLabel()}
          </Button>
          <Button
            size="sm"
            onClick={handleAllocatedToUser}
            style={{ marginBottom: '10px', marginLeft: '10px' }}
            disabled={selectedNumbers?.length > 0}
          >
            Allocated Numbers
          </Button>

          {((!isSuperAdmin && !isReseller) || skipVoicePlanForCallCentre) && (
            <div
              style={{
                background: '#FFF8C6',
                color: '#8B5A2B',
                padding: '10px 15px',
                borderRadius: '6px',
                marginRight: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              <span>
                <strong>Note:</strong> Allocated numbers will use the parent's Applied Voice
                plan by default.
              </span>
            </div>
          )}
        </div>

        {/* Tabs — driven by activeIndex/handleNumberTypeFilter (v1 parity) */}
        <Tabs
          value={String(activeIndex)}
          onValueChange={(v) => {
            const idx = Number(v);
            setActiveIndex(idx);
            handleNumberTypeFilter(['DID', 'VMN', 'TOLL FREE'][idx]);
          }}
          className="mb-3"
        >
          <TabsList>
            {['DID', 'VMN', 'TOLL FREE'].map((tab, index) => (
              <TabsTrigger key={index} value={String(index)}>
                {tab}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <DataTable
          tableId="users.allocate"
          columns={columns}
          data={filteredNumbersList}
          isLoading={loading}
          emptyTitle="No numbers"
          emptyDescription="No numbers match the current filters."
          pageSize={10}
          disableExport
          toolbar={
            <>
              <Select value={trunkFilter || 'all'} onValueChange={(v) => setTrunkFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-8 w-[150px] text-xs" aria-label="Filter by trunk">
                  <SelectValue placeholder="All trunks" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Trunks</SelectItem>
                  {trunkOptions.hasNone && (
                    <SelectItem value={TRUNK_FILTER_NONE}>No trunk</SelectItem>
                  )}
                  {trunkOptions.options.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={categoryFilter || 'all'} onValueChange={(v) => setCategoryFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-8 w-[150px] text-xs">
                  <SelectValue placeholder="Select Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categoryLabels.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={currencyFilter || 'all'} onValueChange={(v) => setCurrencyFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-8 w-[130px] text-xs">
                  <SelectValue placeholder="Select Currency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Currencies</SelectItem>
                  {currencyLabels.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-8 w-[150px] text-xs">
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {NUBMERS_STATUS_LABEL.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          }
        />
      </Card>

      {/* Remove Numbers Modal */}
      <Dialog open={showModal} onOpenChange={(open) => (open ? setShowModal(true) : handleCloseModal())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Numbers</DialogTitle>
          </DialogHeader>
          <div style={{ minHeight: '200px' }}>
            <MultiSelect
              options={(Array.isArray(allocatedNumber) ? allocatedNumber : []).map((n) => ({
                label: n.actual_number,
                value: String(n.id),
              }))}
              value={selectedAllocations.map((v) => String(v))}
              onChange={(next) => setSelectedAllocations(next.map((s) => {
                const orig = allocatedNumber.find((n) => String(n.id) === s);
                return orig ? orig.id : s;
              }))}
              placeholder="Select Number"
            />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={handleCloseModal} className="me-2">
              Close
            </Button>
            <Button onClick={handleRemoveAllocatedDIDs}>Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Voice Plan Allocation Modal */}
      <Dialog open={!!voicePlan} onOpenChange={(open) => (open ? setVoicePlanModel(true) : handleCloseVoicePlanModal())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {comingUserRole === 'company'
                ? 'Allocate Voice Plan To Company'
                : 'Allocate Voice Plan to Call Centers'}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Select
              value={selectedPlan ? String(selectedPlan) : ''}
              onValueChange={(v) => setSelectedVoicePlan(v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Voice Plan" />
              </SelectTrigger>
              <SelectContent>
                {voicePlanOptions.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)} disabled={opt.disabled}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedPlanObjForModal && (
              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-foreground">
                    {selectedPlanObjForModal.plan_name} — Plan Details
                  </span>
                  {selectedPlanObjForModal.is_demo ? (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300">
                      Demo
                    </span>
                  ) : null}
                </div>
                {selectedPlanLegs.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-muted-foreground">
                          <th className="px-2 py-1 font-medium">Call Type / Leg</th>
                          <th className="px-2 py-1 font-medium">Pulse Duration</th>
                          <th className="px-2 py-1 font-medium">Pulse Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedPlanLegs.map((leg, i) => (
                          <tr key={i} className="border-t border-border/50">
                            <td className="px-2 py-1 font-medium text-foreground">
                              {getVoicePlanLabel(leg)}
                            </td>
                            <td className="px-2 py-1">
                              {leg.plan_type === 'MISSED'
                                ? 'N/A'
                                : formatDurationLabel(leg.pulse_duration)}
                            </td>
                            <td className="px-2 py-1">{formatPriceDisplay(leg.pulse_price)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No plan details available.</p>
                )}
              </div>
            )}

            {comingUserRole === 'company' && (
              <Select value={selectedCompany ? String(selectedCompany) : ''} disabled>
                <SelectTrigger>
                  <SelectValue placeholder="Select Company" />
                </SelectTrigger>
                <SelectContent>
                  {(Array.isArray(companyList) ? companyList : []).map((company) => (
                    <SelectItem key={company.id} value={String(company.id)}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {comingUserRole === 'callcentre' && (
              <Select value={selectedCallcenter ? String(selectedCallcenter) : ''} disabled>
                <SelectTrigger>
                  <SelectValue placeholder="Select Callcenter" />
                </SelectTrigger>
                <SelectContent>
                  {(Array.isArray(callcenterList) ? callcenterList : []).map((callcenter) => (
                    <SelectItem key={callcenter.id} value={String(callcenter.id)}>
                      {callcenter.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={handleCloseVoicePlanModal}
              disabled={isAllocating}
              className="me-2"
            >
              Close
            </Button>
            <Button onClick={handleAllocate} disabled={isAllocating}>
              {isAllocating ? 'Allocating...' : 'Allocate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result modal */}
      <Dialog open={showResultModal} onOpenChange={setShowResultModal}>
        <DialogContent style={{ maxWidth: '800px' }}>
          <DialogHeader>
            <DialogTitle>Voice Plan Allocation Result</DialogTitle>
          </DialogHeader>
          {allocationResult && (
            <>
              {allocationResult.successActualNumbers?.length > 0 && (
                <div className="mb-4">
                  <h5 className="text-success mb-2">✅ Voice Plan Allocated To:</h5>
                  <textarea
                    readOnly
                    rows={4}
                    className="form-control"
                    value={allocationResult.successActualNumbers.join('\n')}
                  />
                </div>
              )}
              {allocationResult.failedDIDs?.length > 0 && (
                <div>
                  <h5 className="text-danger mb-2">❌ Voice Plan Allocation Failed:</h5>
                  <textarea
                    readOnly
                    rows={4}
                    className="form-control"
                    value={allocationResult.failedDIDs
                      .map((fail) => `${fail.did} - ${fail.reason}`)
                      .join('\n')}
                  />
                </div>
              )}
            </>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setShowResultModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan Details modal */}
      <Dialog open={showPlanModal} onOpenChange={setShowPlanModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Plan Details - {selectedPlanName}</DialogTitle>
          </DialogHeader>
          {selectedPlanDetails.length > 0 ? (
            <div>
              {selectedPlanDetails.map((plan, index) => (
                <div key={index} style={{ marginBottom: '10px' }}>
                  <strong>{getVoicePlanLabel(plan)}</strong>
                  <br />
                  Pulse Duration: {plan.pulse_duration}
                  <br />
                  Pulse Price: {plan.pulse_price}
                </div>
              ))}
            </div>
          ) : (
            <div>No plan details available.</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Voice Plan modal */}
      <Dialog
        open={!!editVoicePlanModel}
        onOpenChange={(open) => {
          if (!open) {
            setEditVoicePlanModel('');
            setSelectedEditVoicePlan('');
            setCurrentEditPlanName('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Voice Plan</DialogTitle>
          </DialogHeader>
          <Select
            value={selectedEditPlan ? String(selectedEditPlan) : ''}
            onValueChange={(v) => setSelectedEditVoicePlan(v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select Voice Plan" />
            </SelectTrigger>
            <SelectContent>
              {(Array.isArray(voicePlanList) ? voicePlanList : [])
                .filter((plan) => plan.plan_name !== currentEditPlanName)
                .map((plan) => (
                  <SelectItem key={plan.id} value={String(plan.id)}>
                    {plan.plan_name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button
              variant="secondary"
              disabled={isUpdatingPlan}
              className="me-2"
              onClick={() => {
                setEditVoicePlanModel('');
                setSelectedEditVoicePlan('');
                setCurrentEditPlanName('');
              }}
            >
              Close
            </Button>
            <Button onClick={handleUpdateVoicePlan} disabled={isUpdatingPlan}>
              {isUpdatingPlan ? 'Allocating...' : 'Allocate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FillPage>
  );
};

export default UserAllocate;
